import * as React from 'react';
import styled, { css } from 'styled-components';
import Modal from './Modal';
import { PlainButton } from './styled-components';

const OptionButton = styled(PlainButton)<{ selected: boolean }>`
  display: block;
  width: calc(100% + 30px);
  padding: 10px 15px;
  margin: 0 -15px;
  border-radius: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-tap-highlight-color: transparent;

  & + & {
    margin-top: 5px;
  }

  ${({ selected }) =>
    selected &&
    css`
      font-weight: 600;
      background-color: var(--earth-fm--color--dark-beige-40);
    `};
`;

interface Props {
  open: boolean;
  title: string;
  allowMultiple?: boolean;
  options: {
    label: string;
    value: string;
  }[];
  selected: string[];
  onClose: () => void;
  onSelect: (value: string[]) => void;
}

export default function OptionPickerModal({
  open,
  title,
  allowMultiple = true,
  options,
  selected: defaultSelected,
  onClose,
  onSelect,
}: Props) {
  const [selected, setSelected] = React.useState<string[]>(defaultSelected);

  React.useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  const handleOptionClick = (value: string) => {
    if (allowMultiple) {
      if (selected.includes(value)) {
        setSelected((prev) => prev.filter((v) => v !== value));
      } else {
        setSelected((prev) => [...prev, value]);
      }
    } else {
      onSelect([value]);
      onClose();
    }
  };

  const handleApply = React.useCallback(() => {
    onSelect(selected);
    onClose();
  }, [selected, onSelect, onClose]);

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      primaryButton={allowMultiple ? { content: 'Apply', onClick: handleApply } : undefined}
      secondaryButtons={
        allowMultiple
          ? [
              {
                content: 'Cancel',
                onClick: onClose,
              },
            ]
          : undefined
      }
    >
      {options.map((option) => {
        const isChecked = selected.includes(option.value);

        return (
          <OptionButton
            key={option.value}
            selected={isChecked}
            onClick={() => handleOptionClick(option.value)}
          >
            {option.label}
          </OptionButton>
        );
      })}
    </Modal>
  );
}
